import { useState } from 'react';
import DateRangeSelector from '../../../components/DateRangeSelector';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { sendExcel, ReportModel, UserStatus } from '../services/DashboardCRUD';
import { toast } from 'react-toastify';
import { PlanStateEnum } from '../../plans';
import { OrderStateEnum } from '../../orders';

const MAX_DAYS_IN_RANGE = 90;

export default function ExcelReportForm() {
  const intl = useIntl();
  const initialSince = new Date(
    new Date().setDate(new Date().getDate() - new Date().getDay() + 1)
  );
  const initialUntil = new Date(
    new Date().setDate(new Date().getDate() - new Date().getDay() + 7)
  );
  const [since, setSince] = useState(initialSince);
  const [until, setUntil] = useState(initialUntil);
  const [model, setModel] = useState<ReportModel>(ReportModel.PETS);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [userStatus, setUserStatus] = useState<UserStatus | ''>('');
  const [isPetActive, setIsPetActive] = useState<boolean | ''>('');
  const [planState, setPlanState] = useState<PlanStateEnum | ''>('');
  const [orderState, setOrderState] = useState<OrderStateEnum | ''>('');

  const modelOptions = [
    { value: ReportModel.PETS, labelId: 'businessDashboard.modelPets' },
    { value: ReportModel.PLANS, labelId: 'businessDashboard.modelPlans' },
    {
      value: ReportModel.SALEORDERS,
      labelId: 'businessDashboard.modelSaleorders',
    },
    { value: ReportModel.USERS, labelId: 'businessDashboard.modelUsers' },
  ];

  const userStatusOptions = [
    { value: UserStatus.ACTIVE, labelId: 'businessDashboard.statusActive' },
    { value: UserStatus.PAUSED, labelId: 'businessDashboard.statusPaused' },
    { value: UserStatus.PROPOSAL, labelId: 'businessDashboard.statusProposal' },
  ];

  const petActiveOptions = [
    { value: 'true', labelId: 'businessDashboard.activePet' },
    { value: 'false', labelId: 'businessDashboard.inactivePet' },
  ];

  const getModelLabel = (model: ReportModel): string => {
    const modelLabels = {
      [ReportModel.PETS]: intl.formatMessage({
        id: 'businessDashboard.modelPets',
      }),
      [ReportModel.PLANS]: intl.formatMessage({
        id: 'businessDashboard.modelPlans',
      }),
      [ReportModel.SALEORDERS]: intl.formatMessage({
        id: 'businessDashboard.modelSaleorders',
      }),
      [ReportModel.USERS]: intl.formatMessage({
        id: 'businessDashboard.modelUsers',
      }),
    };
    return modelLabels[model];
  };

  async function generateReport() {
    const hasConfirmed = window.confirm(
      intl.formatMessage(
        { id: 'businessDashboard.confirmExcelReport' },
        {
          model: getModelLabel(model).toLowerCase(),
          since: moment(since).format('DD/MM/YYYY'),
          until: moment(until).format('DD/MM/YYYY'),
        }
      )
    );

    if (hasConfirmed) {
      try {
        setGeneratingReport(true);
        await sendExcel({
          startDate: moment(since).format('YYYY-MM-DD'),
          endDate: moment(until).format('YYYY-MM-DD'),
          model,
          userStatus: userStatus || undefined,
          isPetActive: isPetActive === '' ? undefined : isPetActive,
          planState: planState || undefined,
          orderState: orderState || undefined,
        });
        toast.success(
          intl.formatMessage({ id: 'businessDashboard.excelReportGenerated' })
        );
      } catch (error) {
        toast.error(
          intl.formatMessage({ id: 'businessDashboard.excelReportError' })
        );
        console.warn(error);
      } finally {
        setGeneratingReport(false);
      }
    }
  }

  return (
    <>
      <h1 className="title mb-8">
        {intl.formatMessage({ id: 'businessDashboard.generateExcelReport' })}
      </h1>
      <div className="d-flex gap-4 mb-4">
        <DateRangeSelector
          initialSince={initialSince}
          initialUntil={initialUntil}
          onSelected={(since, until) => {
            setSince(since);
            setUntil(until);
          }}
          disableFutureDates={false}
        />
        <div className="form-group">
          <label className="form-label">
            {intl.formatMessage({ id: 'businessDashboard.selectModel' })}
          </label>
          <select
            className="form-select"
            value={model}
            onChange={(e) => setModel(e.target.value as ReportModel)}
          >
            {modelOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {intl.formatMessage({ id: option.labelId })}
              </option>
            ))}
          </select>
        </div>
        {model === ReportModel.USERS && (
          <div className="form-group">
            <label className="form-label">
              {intl.formatMessage({ id: 'businessDashboard.userStatus' })}
            </label>
            <select
              className="form-select"
              value={userStatus}
              onChange={(e) => setUserStatus(e.target.value as UserStatus | '')}
            >
              <option value="">
                {intl.formatMessage({ id: 'businessDashboard.allStatuses' })}
              </option>
              {userStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {intl.formatMessage({ id: option.labelId })}
                </option>
              ))}
            </select>
          </div>
        )}
        {model === ReportModel.PETS && (
          <div className="form-group">
            <label className="form-label">
              {intl.formatMessage({ id: 'businessDashboard.petActive' })}
            </label>
            <select
              className="form-select"
              value={isPetActive === '' ? '' : isPetActive.toString()}
              onChange={(e) => {
                const value = e.target.value;
                setIsPetActive(value === '' ? '' : value === 'true');
              }}
            >
              <option value="">
                {intl.formatMessage({ id: 'businessDashboard.all' })}
              </option>
              {petActiveOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {intl.formatMessage({ id: option.labelId })}
                </option>
              ))}
            </select>
          </div>
        )}
        {model === ReportModel.PLANS && (
          <div className="form-group">
            <label className="form-label">
              {intl.formatMessage({ id: 'businessDashboard.planState' })}
            </label>
            <select
              className="form-select"
              value={planState}
              onChange={(e) =>
                setPlanState(e.target.value as PlanStateEnum | '')
              }
            >
              <option value="">
                {intl.formatMessage({ id: 'businessDashboard.allStates' })}
              </option>
              {Object.values(PlanStateEnum).map((state) => (
                <option key={state} value={state}>
                  {intl.formatMessage({ id: `planState.${state}` })}
                </option>
              ))}
            </select>
          </div>
        )}
        {model === ReportModel.SALEORDERS && (
          <div className="form-group">
            <label className="form-label">
              {intl.formatMessage({ id: 'businessDashboard.orderState' })}
            </label>
            <select
              className="form-select"
              value={orderState}
              onChange={(e) =>
                setOrderState(e.target.value as OrderStateEnum | '')
              }
            >
              <option value="">
                {intl.formatMessage({ id: 'businessDashboard.allStates' })}
              </option>
              {Object.values(OrderStateEnum).map((state) => (
                <option key={state} value={state}>
                  {intl.formatMessage({ id: `orderState.${state}` })}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="d-flex gap-4 align-items-center mb-4">
        <button
          className="btn btn-primary"
          disabled={
            generatingReport ||
            moment(until).diff(moment(since), 'days') > MAX_DAYS_IN_RANGE
          }
          onClick={generateReport}
        >
          {generatingReport ? (
            <span className="spinner-border spinner-border-sm me-3"></span>
          ) : (
            <span className="fas fa-file-excel me-3"></span>
          )}
          {intl.formatMessage({ id: 'businessDashboard.generateExcelReport' })}
        </button>
      </div>
      {moment(until).diff(moment(since), 'days') > MAX_DAYS_IN_RANGE && (
        <p className="mt-4 text-danger fw-bolder">
          {intl.formatMessage(
            { id: 'businessDashboard.dateRangeExceedsLimit' },
            { maxDays: MAX_DAYS_IN_RANGE }
          )}
        </p>
      )}
    </>
  );
}
